import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { onAuthStateChanged, User as FirebaseUser } from 'firebase/auth';
import { auth } from '@/firebase';
// Dynamic Firestore imports to prevent bundle bloat
import { UseAuthReturn, ExpertApplication, AuthUser, UserType } from '../types';

interface AuthContextType extends UseAuthReturn {
  refreshExpertStatus: () => Promise<void>;
  updateUserDisplayName: (newFirstName: string, newLastName?: string) => void;
  refreshUserProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  // Core authentication state
  const [user, setUser] = useState<AuthUser | null>(null);
  const [authUser, setAuthUser] = useState<FirebaseUser | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isCheckingAuth, setIsCheckingAuth] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // User profile information
  const [userName, setUserName] = useState<string>('');
  const [userEmail, setUserEmail] = useState<string>('');
  const [displayName, setDisplayName] = useState<string>('');
  const [isEmailVerified, setIsEmailVerified] = useState<boolean>(false);

  // Expert status information
  const [isExpert, setIsExpert] = useState<boolean>(false);
  const [isApplicationApproved, setIsApplicationApproved] = useState<boolean>(false);
  const [expertData, setExpertData] = useState<ExpertApplication | null>(null);

  // Admin status information
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  // Additional user data
  const [points, setPoints] = useState<number>(0);

  // Compute user type based on current state
  const getUserType = (): UserType => {
    if (!isLoggedIn) {return 'user';}
    if (isAdmin) {return 'admin';}
    if (isExpert && isApplicationApproved) {return 'expert';}
    if (isExpert && !isApplicationApproved) {return 'pending_expert';}
    return 'user';
  };

  // Helper function to convert Firebase user to our AuthUser type
  const createAuthUser = (firebaseUser: FirebaseUser): AuthUser => ({
    uid: firebaseUser.uid,
    email: firebaseUser.email,
    displayName: firebaseUser.displayName,
    photoURL: firebaseUser.photoURL,
    emailVerified: firebaseUser.emailVerified,
  });

  // Helper function to check admin status
  const checkAdminStatus = async (uid: string): Promise<void> => {
    try {
      const { getFirestoreLite } = await import('@/firebase/firestore-lite');
      const db = getFirestoreLite();
      const { doc, getDoc } = await import('firebase/firestore/lite');
      const adminDocRef = doc(db, 'admins', uid);
      const adminDocSnap = await getDoc(adminDocRef);
      const adminExists = adminDocSnap.exists();
      setIsAdmin(adminExists);
    } catch (error) {
      setIsAdmin(false);
    }
  };

  // Helper function to refresh user profile data (for after signup/profile updates)
  const refreshUserProfile = async (): Promise<void> => {
    if (authUser) {
      try {
        // Reload the Firebase user to get latest data
        await authUser.reload();
        
        const { displayName, emailVerified, email, uid } = authUser;
        const firstName = displayName ? displayName.split(' ')[0] : '';

        // Update core auth state
        setIsEmailVerified(emailVerified);
        setUserEmail(email || '');
        setDisplayName(displayName || '');

        // Check admin status
        await checkAdminStatus(uid);

        // Fetch user profile data from Firestore
        const { getFirestoreLite } = await import('@/firebase/firestore-lite');
        const db = getFirestoreLite();
        const { doc, getDoc } = await import('firebase/firestore/lite');
        
        // Check expert status first
        const expertDocRef = doc(db, 'expertApplications', uid);
        const expertDocSnap = await getDoc(expertDocRef);

        if (expertDocSnap.exists()) {
          const expertInfo = expertDocSnap.data() as ExpertApplication;
          setIsApplicationApproved(expertInfo.approved || false);
          setIsExpert(true);
          setExpertData(expertInfo);
          setPoints(expertInfo.points || 0);
          
          const expertFirstName = expertInfo.firstName || firstName;
          const expertDisplayName = expertInfo.displayName || `${expertInfo.firstName || ''} ${expertInfo.lastName || ''}`.trim() || displayName;
          setUserName(expertFirstName);
          setDisplayName(expertDisplayName);
        } else {
          setIsApplicationApproved(false);
          setIsExpert(false);
          setExpertData(null);

          // Fetch user profile data
          const userDocRef = doc(db, 'userProfiles', uid);
          const userDocSnap = await getDoc(userDocRef);
          
          if (userDocSnap.exists()) {
            const userData = userDocSnap.data();
            setPoints(userData.points || 0);
            
            const userFirstName = userData.firstName || firstName;
            const userDisplayName = userData.displayName || `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || displayName;
            setUserName(userFirstName);
            setDisplayName(userDisplayName);
          } else {
            // Fallback to Firebase Auth data
            setUserName(firstName);
            setPoints(0);
          }
        }
      } catch (error) {
        console.error('Error refreshing user profile:', error);
        setError(error instanceof Error ? error.message : 'Failed to refresh user profile');
      }
    }
  };

  // Helper function to refresh expert status (useful after profile updates)
  const refreshExpertStatus = async (): Promise<void> => {
    // Just call refreshUserProfile since it does the same thing
    await refreshUserProfile();
  };

  // Single authentication listener - THIS RUNS ONCE FOR ALL COMPONENTS
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser: FirebaseUser | null) => {
      setIsCheckingAuth(true);
      setError(null);

      if (currentUser) {
        try {
          // User is authenticated
          const { displayName, emailVerified, email, uid } = currentUser;
          const firstName = displayName ? displayName.split(' ')[0] : '';

          // Set core auth state
          const authUserData = createAuthUser(currentUser);
          setUser(authUserData);
          setAuthUser(currentUser);
          setIsLoggedIn(true);
          setIsEmailVerified(emailVerified);
          setUserEmail(email || '');
          setUserName(firstName);
          setDisplayName(displayName || '');

          // CRITICAL FIX: These queries now run ONCE, not 64+ times
          // Check admin status first
          await checkAdminStatus(uid);

          // Check expert status
          const { getFirestoreLite } = await import('@/firebase/firestore-lite');
      const db = getFirestoreLite();
          const { doc, getDoc } = await import('firebase/firestore/lite');
          const expertDocRef = doc(db, 'expertApplications', uid);
          const expertDocSnap = await getDoc(expertDocRef);

          if (expertDocSnap.exists()) {
            const expertInfo = expertDocSnap.data() as ExpertApplication;
            setIsApplicationApproved(expertInfo.approved || false);
            setIsExpert(true);
            setExpertData(expertInfo);
            // Set points from expert profile (expert points take priority)
            const expertPoints = expertInfo.points || 0;
            setPoints(expertPoints);
            
            // Use expert's name from profile, fallback to Firebase Auth
            const expertFirstName = expertInfo.firstName || firstName;
            const expertDisplayName = expertInfo.displayName || `${expertInfo.firstName || ''} ${expertInfo.lastName || ''}`.trim() || displayName;
            setUserName(expertFirstName);
            setDisplayName(expertDisplayName);
          } else {
            setIsApplicationApproved(false);
            setIsExpert(false);
            setExpertData(null);

            // Fetch user points from profile only if not an expert
            let userPoints = 0;
            try {
              const { doc, getDoc } = await import('firebase/firestore/lite');
              const userDocRef = doc(db, 'userProfiles', uid);
              const userDocSnap = await getDoc(userDocRef);
              if (userDocSnap.exists()) {
                const userData = userDocSnap.data();
                userPoints = userData.points || 0;
                
                // Also get user's name from profile
                const userFirstName = userData.firstName || firstName;
                const userDisplayName = userData.displayName || `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || displayName;
                setUserName(userFirstName);
                setDisplayName(userDisplayName);
              }
            } catch (error) {
              // Silently handle error
            }
            setPoints(userPoints);
          }
        } catch (error) {
          setError(error instanceof Error ? error.message : 'Authentication error');
          setIsApplicationApproved(false);
          setIsExpert(false);
          setExpertData(null);
        }
      } else {
        // User is not authenticated - clear all state
        setUser(null);
        setAuthUser(null);
        setIsLoggedIn(false);
        setIsEmailVerified(false);
        setUserEmail('');
        setUserName('');
        setDisplayName('');
        setPoints(0);
        setIsApplicationApproved(false);
        setIsExpert(false);
        setExpertData(null);
        setIsAdmin(false);
      }

      setIsLoading(false);
      setIsCheckingAuth(false);
    });

    // Cleanup function to prevent memory leaks
    return () => unsubscribe();
  }, []); // Empty dependency array - this effect should only run once

  // Function to update user display name without refetching
  const updateUserDisplayName = useCallback((newFirstName: string, newLastName?: string) => {
    setUserName(newFirstName);
    
    // Update displayName based on what we have
    if (newLastName !== undefined) {
      setDisplayName(`${newFirstName} ${newLastName}`.trim());
    } else if (expertData?.lastName) {
      setDisplayName(`${newFirstName} ${expertData.lastName}`.trim());
    } else if (displayName && displayName.includes(' ')) {
      // Keep existing last name if we have it
      const existingLastName = displayName.split(' ').slice(1).join(' ');
      setDisplayName(`${newFirstName} ${existingLastName}`.trim());
    } else {
      setDisplayName(newFirstName);
    }
  }, [expertData, displayName]);

  // Context value with all auth state and helper functions
  const value: AuthContextType = {
    // Core authentication state
    user,
    isLoggedIn,
    isLoading,
    error,

    // Expert status
    isExpert: isExpert && isApplicationApproved, // Only true if approved
    expertData,
    
    // Admin status
    isAdmin,
    
    userProfile: null, // TODO: Implement user profile fetching
    userType: getUserType(),

    // Legacy support - keeping these for backward compatibility during migration
    authUser,
    isCheckingAuth,
    userName,
    userEmail,
    displayName,
    isEmailVerified,
    isApplicationApproved,
    points,
    refreshExpertStatus,
    updateUserDisplayName,
    refreshUserProfile,

    // Computed values for common use cases
    isAuthenticated: isLoggedIn && user !== null,
    isVerifiedExpert: isLoggedIn && isApplicationApproved,
    firstName: userName,

    // Loading states for UI
    isSpinnerShowing: isCheckingAuth,
    buttonText: isCheckingAuth
      ? null
      : (userName || 'Login')
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthProvider;
